import { signInWithCustomToken } from "firebase/auth";
import { auth, storage } from "../firebase/firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  uploadBytesResumable,
} from "firebase/storage";

// Firebase Functions URL
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_URL || "https://api-bow3rmzs7q-uc.a.run.app";

// 카카오 앱 키
const KAKAO_APP_KEY =
  process.env.REACT_APP_KAKAO_APP_KEY || "5985c4c8d45d460e2436730e930789a9";

// 네이버 앱 키
const NAVER_CLIENT_ID =
  process.env.REACT_APP_NAVER_CLIENT_ID || "Qmbfqw32fueDm8oNDyqZ";

// 카카오 로그인 응답 타입
interface KakaoLoginResponse {
  success: boolean;
  customToken?: string;
  user?: {
    uid: string;
    email: string | null;
    displayName: string;
    photoURL: string | null;
  };
  requiresSignup?: boolean;
  kakaoInfo?: {
    kakaoId: number;
    email: string | null;
    name: string | null;
    profileImage: string | null;
  };
  message?: string;
  error?: string;
}

// 네이버 로그인 응답 타입
interface NaverLoginResponse {
  success: boolean;
  customToken?: string;
  user?: {
    uid: string;
    email: string | null;
    displayName: string;
    photoURL: string | null;
  };
  requiresSignup?: boolean;
  naverInfo?: {
    naverId: string;
    email: string | null;
    name: string | null;
    profileImage: string | null;
  };
  message?: string;
  error?: string;
}

// 카카오 회원가입 응답 타입
interface KakaoSignupResponse {
  success: boolean;
  customToken?: string;
  user?: {
    uid: string;
    email: string;
    displayName: string;
    photoURL: string | null;
  };
  message?: string;
  error?: string;
}

// 네이버 회원가입 응답 타입
interface NaverSignupResponse {
  success: boolean;
  customToken?: string;
  user?: {
    uid: string;
    email: string;
    displayName: string;
    photoURL: string | null;
  };
  message?: string;
  error?: string;
}

// 카카오 회원가입 데이터 타입
interface KakaoSignupData {
  kakaoId: number;
  email: string;
  name: string;
  phone: string;
  profileImage?: string;
  petInfo?: any;
}

// 네이버 회원가입 데이터 타입
interface NaverSignupData {
  naverId: string;
  email: string;
  name: string;
  phone: string;
  profileImage?: string;
  petInfo?: any;
}

// 카카오 로그인 시작 (리다이렉트)
export const startKakaoLogin = (): void => {
  const redirectUri = encodeURIComponent(window.location.origin + "/login");
  const state = Math.random().toString(36).substring(2, 15);

  // CSRF 방지를 위한 state 저장
  localStorage.setItem("kakao_state", state);

  // 카카오 인증 URL 구성 (더 상세한 scope과 안전한 로그인 URL)
  const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?response_type=code&scope=account_email,profile_nickname,profile_image&client_id=${KAKAO_APP_KEY}&redirect_uri=${redirectUri}&state=${state}`;

  // 카카오 로그인 페이지를 거쳐서 인증하도록 URL 구성
  const kakaoLoginUrl = `https://accounts.kakao.com/login/?continue=${encodeURIComponent(
    kakaoAuthUrl
  )}`;

  window.location.href = kakaoLoginUrl;
};

// URL에서 인가 코드 추출
export const getKakaoCodeFromURL = (): {
  code: string | null;
  state: string | null;
} => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    code: urlParams.get("code"),
    state: urlParams.get("state"),
  };
};

// 카카오 리다이렉트 콜백 처리
export const handleKakaoCallback = async (): Promise<KakaoLoginResponse> => {
  const { code, state } = getKakaoCodeFromURL();
  const savedState = localStorage.getItem("kakao_state");

  if (!code) {
    throw new Error("인가 코드가 없습니다.");
  }

  // CSRF 검증
  if (state !== savedState) {
    throw new Error("잘못된 요청입니다.");
  }

  // state 정리
  localStorage.removeItem("kakao_state");

  // URL에서 파라미터 제거
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, document.title, url.toString());

  try {
    // Firebase Functions로 인가 코드 전송
    const response = await fetch(`${FUNCTIONS_BASE_URL}/kakao-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });

    const result: KakaoLoginResponse = await response.json();

    if (!result.success && result.requiresSignup) {
      // 회원가입 필요한 경우
      return result;
    }

    if (!result.success || !result.customToken) {
      throw new Error(result.error || "로그인 처리 실패");
    }

    // Firebase 로그인
    const userCredential = await signInWithCustomToken(
      auth,
      result.customToken
    );

    return {
      success: true,
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName || "",
        photoURL: userCredential.user.photoURL,
      },
    };
  } catch (error) {
    console.error("카카오 로그인 처리 오류:", error);
    throw error;
  }
};

// 카카오 회원가입
export const signupWithKakao = async (
  signupData: KakaoSignupData
): Promise<KakaoSignupResponse> => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/kakao-signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(signupData),
    });

    const result: KakaoSignupResponse = await response.json();

    if (!result.success || !result.customToken) {
      throw new Error(result.error || "회원가입 처리 실패");
    }

    // Firebase 로그인
    const userCredential = await signInWithCustomToken(
      auth,
      result.customToken
    );

    return {
      success: true,
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email || "",
        displayName: userCredential.user.displayName || "",
        photoURL: userCredential.user.photoURL,
      },
      message: result.message,
    };
  } catch (error) {
    console.error("카카오 회원가입 오류:", error);
    throw error;
  }
};

// 네이버 로그인 시작 (리다이렉트)
export const startNaverLogin = (): void => {
  const redirectUri = encodeURIComponent(window.location.origin + "/login");
  const state = Math.random().toString(36).substring(2, 15);

  // CSRF 방지를 위한 state 저장
  localStorage.setItem("naver_state", state);

  const naverAuthUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${NAVER_CLIENT_ID}&redirect_uri=${redirectUri}&state=${state}`;

  window.location.href = naverAuthUrl;
};

// URL에서 네이버 인가 코드 추출
export const getNaverCodeFromURL = (): {
  code: string | null;
  state: string | null;
} => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    code: urlParams.get("code"),
    state: urlParams.get("state"),
  };
};

// 네이버 리다이렉트 콜백 처리
export const handleNaverCallback = async (): Promise<NaverLoginResponse> => {
  const { code, state } = getNaverCodeFromURL();
  const savedState = localStorage.getItem("naver_state");

  if (!code) {
    throw new Error("인가 코드가 없습니다.");
  }

  // CSRF 검증
  if (state !== savedState) {
    throw new Error("잘못된 요청입니다.");
  }

  // state 정리
  localStorage.removeItem("naver_state");

  // URL에서 파라미터 제거
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, document.title, url.toString());

  try {
    // Firebase Functions로 인가 코드 전송
    const response = await fetch(`${FUNCTIONS_BASE_URL}/naver-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });

    const result: NaverLoginResponse = await response.json();

    if (!result.success && result.requiresSignup) {
      // 회원가입 필요한 경우
      return result;
    }

    if (!result.success || !result.customToken) {
      throw new Error(result.error || "로그인 처리 실패");
    }

    // Firebase 로그인
    const userCredential = await signInWithCustomToken(
      auth,
      result.customToken
    );

    return {
      success: true,
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName || "",
        photoURL: userCredential.user.photoURL,
      },
    };
  } catch (error) {
    console.error("네이버 로그인 처리 오류:", error);
    throw error;
  }
};

// 네이버 회원가입
export const signupWithNaver = async (
  signupData: NaverSignupData
): Promise<NaverSignupResponse> => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/naver-signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(signupData),
    });

    const result: NaverSignupResponse = await response.json();

    if (!result.success || !result.customToken) {
      throw new Error(result.error || "회원가입 처리 실패");
    }

    // Firebase 로그인
    const userCredential = await signInWithCustomToken(
      auth,
      result.customToken
    );

    return {
      success: true,
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email || "",
        displayName: userCredential.user.displayName || "",
        photoURL: userCredential.user.photoURL,
      },
      message: result.message,
    };
  } catch (error) {
    console.error("네이버 회원가입 오류:", error);
    throw error;
  }
};

// 네이버 로그인 (기존 함수 업데이트)
export const loginWithNaver = async (): Promise<any> => {
  throw new Error(
    "startNaverLogin을 사용하세요. 네이버는 리다이렉트 방식으로 로그인합니다."
  );
};

// 소셜 로그인 제공자별 로그아웃
export const logoutFromSocialProvider = async (
  provider: string
): Promise<void> => {
  // 소셜 로그인 프로바이더별 로그아웃 처리
  if (provider === "kakao") {
    // 카카오 로그아웃은 별도 처리 필요시 여기에 구현
  } else if (provider === "naver") {
    // 네이버 로그아웃은 별도 처리 필요시 여기에 구현
  }
};

// 피드 작성 관련 API 함수들
export const createFeed = async (feedData: {
  content: string;
  hashtags: string[];
  locationInfo?: {
    latitude?: number;
    longitude?: number;
    address?: string;
  };
  mediaFiles?: any[];
}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("로그인이 필요합니다.");
    }

    const idToken = await user.getIdToken();

    const response = await fetch(`${FUNCTIONS_BASE_URL}/create-feed`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(feedData),
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "피드 작성에 실패했습니다.");
    }

    return result;
  } catch (error) {
    console.error("피드 작성 오류:", error);
    throw error;
  }
};

// 피드 목록 조회
export const getFeeds = async (
  params: {
    limit?: number;
    lastKey?: string;
    category?: "all" | "user";
    userId?: string;
  } = {}
) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.lastKey) queryParams.append("lastKey", params.lastKey);
    if (params.category) queryParams.append("category", params.category);
    if (params.userId) queryParams.append("userId", params.userId);

    const response = await fetch(`${FUNCTIONS_BASE_URL}/feeds?${queryParams}`);
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "피드 목록 조회에 실패했습니다.");
    }

    return result;
  } catch (error) {
    console.error("피드 목록 조회 오류:", error);
    throw error;
  }
};

// 인기 해시태그 조회
export const getPopularHashtags = async (limit: number = 10) => {
  try {
    const response = await fetch(
      `${FUNCTIONS_BASE_URL}/hashtags/popular?limit=${limit}`
    );
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "인기 해시태그 조회에 실패했습니다.");
    }

    return result.hashtags;
  } catch (error) {
    console.error("인기 해시태그 조회 오류:", error);
    throw error;
  }
};

// 해시태그 검색
export const searchHashtags = async (query: string, limit: number = 10) => {
  try {
    const response = await fetch(
      `${FUNCTIONS_BASE_URL}/hashtags/search?query=${encodeURIComponent(
        query
      )}&limit=${limit}`
    );
    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "해시태그 검색에 실패했습니다.");
    }

    return result.hashtags;
  } catch (error) {
    console.error("해시태그 검색 오류:", error);
    throw error;
  }
};

// 파일 업로드 함수 (개선된 에러 처리)
export const uploadMediaFile = async (
  file: File,
  userId: string,
  feedId: string,
  index: number
): Promise<string> => {
  const maxRetries = 3;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      // 임시 경로 사용 (더 관대한 권한)
      const useTemporaryPath = true;
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileName = `${timestamp}_${index}_${randomId}.${file.name
        .split(".")
        .pop()}`;

      const storagePath = useTemporaryPath
        ? `temp/${userId}/${fileName}`
        : `feeds/${userId}/${feedId}/${fileName}`;

      const storageRef = ref(storage, storagePath);

      // 메타데이터 설정 (한국 리전 최적화)
      const metadata = {
        contentType: file.type,
        cacheControl: "public, max-age=31536000",
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          userId: userId,
          feedId: feedId,
          region: "ASIA-NORTHEAST3",
        },
      };

      try {
        // 한국 리전 최적화된 Resumable Upload
        const uploadTask = uploadBytesResumable(storageRef, file, metadata);

        const uploadPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("업로드 타임아웃"));
          }, 180000); // 3분

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              // 진행률 로그 제거 - 성능 최적화
              if (snapshot.bytesTransferred === snapshot.totalBytes) {
                clearTimeout(timeout);
              }
            },
            (error) => {
              clearTimeout(timeout);
              reject(error);
            },
            () => {
              clearTimeout(timeout);
              resolve(uploadTask.snapshot);
            }
          );
        });

        const snapshot = await uploadPromise;
        const downloadURL = await getDownloadURL((snapshot as any).ref);
        return downloadURL;
      } catch (resumableError: any) {
        // CORS 에러인 경우 기본 방식 시도
        if (
          resumableError.message?.includes("CORS") ||
          resumableError.message?.includes("preflight")
        ) {
          try {
            const simpleSnapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(simpleSnapshot.ref);
            return downloadURL;
          } catch (basicError: any) {
            throw resumableError;
          }
        } else {
          throw resumableError;
        }
      }
    } catch (error: any) {
      // 재시도 가능한 오류인지 확인
      const retryableErrors = [
        "storage/unknown",
        "storage/retry-limit-exceeded",
        "storage/canceled",
        "업로드 타임아웃",
        "CORS",
        "preflight",
      ];

      const shouldRetry = retryableErrors.some(
        (errorType) =>
          error.code === errorType || error.message?.includes(errorType)
      );

      if (shouldRetry && retryCount < maxRetries - 1) {
        retryCount++;
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // 최종 실패 - 사용자 친화적 에러 메시지
      const errorMessage =
        error.code === "storage/unauthorized"
          ? "파일 업로드 권한이 없습니다. 로그인 상태를 확인해주세요."
          : error.code === "storage/invalid-format"
          ? "지원되지 않는 파일 형식입니다."
          : error.message?.includes("CORS")
          ? "네트워크 연결 문제가 발생했습니다. 잠시 후 다시 시도해주세요."
          : "파일 업로드에 실패했습니다. 다시 시도해주세요.";

      throw new Error(errorMessage);
    }
  }

  throw new Error("파일 업로드에 실패했습니다.");
};

// 여러 미디어 파일 업로드 함수
export const uploadMultipleMediaFiles = async (
  mediaFiles: Array<{ file: File; optimized?: File; type: string }>,
  userId: string,
  feedId: string
): Promise<
  Array<{ url: string; type: string; name: string; size: number }>
> => {
  try {
    const uploadPromises = mediaFiles.map(async (media, index) => {
      // 최적화된 파일이 있으면 사용, 없으면 원본 사용
      const fileToUpload = media.optimized || media.file;
      const downloadURL = await uploadMediaFile(
        fileToUpload,
        userId,
        feedId,
        index
      );

      return {
        url: downloadURL,
        type: media.type,
        name: media.file.name,
        size: fileToUpload.size,
      };
    });

    const uploadedFiles = await Promise.all(uploadPromises);
    return uploadedFiles;
  } catch (error) {
    console.error("다중 파일 업로드 오류:", error);
    throw error;
  }
};
